// Web: the browser's Web Speech API for hands-free cook mode.
// Recognition runs on the device where the browser can (Chrome's on-device models: `processLocally`, asked for
// with SpeechRecognition.available/install). Otherwise the browser would send the audio to its own speech
// service (Google for Chrome, Apple for Safari), so cook mode asks the person first ('service' mode) and the
// privacy policy says so. Chefi's server never receives audio. Speaking uses the system voices (speechSynthesis).
export type SpeechMode = 'device' | 'service' | 'none';
export type Listener = { stop: () => void };

type Recognition = {
  lang: string; continuous: boolean; interimResults: boolean; processLocally?: boolean;
  onresult: ((e: { resultIndex: number; results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  start: () => void; stop: () => void; abort: () => void;
};
type RecognitionClass = (new () => Recognition) & {
  available?: (o: { langs: string[]; processLocally: boolean }) => Promise<string>;
  install?: (o: { langs: string[]; processLocally: boolean }) => Promise<boolean>;
};

const g = globalThis as unknown as { SpeechRecognition?: RecognitionClass; webkitSpeechRecognition?: RecognitionClass; speechSynthesis?: SpeechSynthesis };
const Ctor = typeof window === 'undefined' ? undefined : g.SpeechRecognition ?? g.webkitSpeechRecognition;

export const speechSupported = !!Ctor;
export const speakSupported = typeof window !== 'undefined' && !!g.speechSynthesis;

/** 'device' when this browser can recognise `lang` on the device (installing the model if needed), else 'service' or 'none'. */
export async function recognitionMode(lang: string): Promise<SpeechMode> {
  if (!Ctor) return 'none';
  if (!Ctor.available) return 'service';
  try {
    const state = await Ctor.available({ langs: [lang], processLocally: true });
    if (state === 'available') return 'device';
    if ((state === 'downloadable' || state === 'downloading') && Ctor.install && (await Ctor.install({ langs: [lang], processLocally: true }))) return 'device';
  } catch {
    // Older implementations: fall through.
  }
  return 'service';
}

/** Listens until stopped, restarting after the pauses browsers end a session on. Each final phrase goes to onPhrase. */
export function listen(o: { lang: string; mode: SpeechMode; onPhrase: (text: string) => void; onError: (code: string) => void; onEnd: () => void }): Listener {
  if (!Ctor || o.mode === 'none') {
    o.onError('unsupported');
    return { stop: () => {} };
  }
  let active = true;
  let rec: Recognition | null = null;
  const start = () => {
    rec = new Ctor();
    rec.lang = o.lang;
    rec.continuous = true;
    rec.interimResults = false;
    if (o.mode === 'device') rec.processLocally = true;
    rec.onresult = e => {
      for (let i = e.resultIndex; i < e.results.length; i++) if (e.results[i].isFinal) o.onPhrase(e.results[i][0].transcript);
    };
    rec.onerror = e => {
      if (e.error === 'no-speech' || e.error === 'aborted') return;
      active = false;
      o.onError(e.error);
    };
    rec.onend = () => (active ? setTimeout(() => active && start(), 250) : o.onEnd());
    try {
      rec.start();
    } catch {
      active = false;
      o.onError('start_failed');
    }
  };
  start();
  return {
    stop: () => {
      active = false;
      rec?.abort();
    },
  };
}

export function speak(text: string, lang: string): void {
  if (!speakSupported || !text) return;
  const s = g.speechSynthesis!;
  s.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = lang;
  s.speak(u);
}

export function stopSpeaking(): void {
  if (speakSupported) g.speechSynthesis!.cancel();
}
